import i18n from '../i18n';
import {
    getCountries,
    getCountryCallingCode,
    parsePhoneNumberFromString,
} from 'libphonenumber-js/max';
import { getDefaultPhoneCountryIso, getRegionalDefaults } from './regionalDefaults';
import { getUiLocaleTag } from './regionalFormat';

/** Fallback for unit tests and pre-setup — runtime uses regional_settings.countryCode. */
export const DEFAULT_PHONE_COUNTRY = 'IN';
export const DEFAULT_COUNTRY_CODE = '+91';

function resolvedDefaultIso() {
    return getDefaultPhoneCountryIso() || DEFAULT_PHONE_COUNTRY;
}

let cachedCountryOptions = null;
let cachedPreferredIso = null;
let cachedUiLocale = null;

/**
 * Country calling codes from libphonenumber metadata (Google / ITU).
 * Update the npm package periodically — no manual country list maintenance.
 * Display names come from the browser Intl API (CLDR) in the selected UI language.
 */
export function getPhoneCountryCodeOptions() {
    const preferredIso = resolvedDefaultIso();
    const uiLocale = getUiLocaleTag();
    if (
        cachedCountryOptions
        && cachedPreferredIso === preferredIso
        && cachedUiLocale === uiLocale
    ) {
        return cachedCountryOptions;
    }

    const displayNames =
        typeof Intl !== 'undefined' && Intl.DisplayNames
            ? new Intl.DisplayNames([uiLocale], { type: 'region' })
            : null;

    const allOptions = getCountries()
        .map((iso) => {
            const dialCode = getCountryCallingCode(iso);
            const name = displayNames?.of(iso) || iso;
            return {
                iso,
                countryCode: `+${dialCode}`,
                name,
                label: `${name} (+${dialCode})`,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name, uiLocale));

    const preferred = allOptions.find((opt) => opt.iso === preferredIso);
    const rest = allOptions.filter((opt) => opt.iso !== preferredIso);
    cachedCountryOptions = preferred ? [preferred, ...rest] : allOptions;
    cachedPreferredIso = preferredIso;
    cachedUiLocale = uiLocale;

    return cachedCountryOptions;
}

export function getCountryCodeForIso(iso) {
    if (!iso) {
        const dial = getRegionalDefaults().phoneDialCode;
        return dial || DEFAULT_COUNTRY_CODE;
    }
    try {
        return `+${getCountryCallingCode(iso)}`;
    } catch {
        const dial = getRegionalDefaults().phoneDialCode;
        return dial || DEFAULT_COUNTRY_CODE;
    }
}

/** Strip duplicate country calling code or trunk 0 from local digits while typing. */
export function normalizeLocalDigits(iso, local) {
    let digits = (local || '').replace(/\D/g, '');
    if (!iso || !digits) return digits;

    try {
        const asInternational = parsePhoneNumberFromString(`+${digits}`, iso);
        if (asInternational?.country === iso && asInternational.nationalNumber) {
            return asInternational.nationalNumber;
        }

        const dialCode = getCountryCallingCode(iso);
        if (digits.startsWith(dialCode) && digits.length > dialCode.length) {
            const withoutCode = digits.slice(dialCode.length);
            const asNational = parsePhoneNumberFromString(withoutCode, iso);
            if (asNational?.country === iso) {
                return withoutCode;
            }
        }

        if (digits.startsWith('0') && digits.length > 1) {
            const withoutZero = digits.slice(1);
            const asNational = parsePhoneNumberFromString(withoutZero, iso);
            if (asNational?.country === iso) {
                return withoutZero;
            }
        }
    } catch {
        // fall through
    }

    return digits;
}

/**
 * Split stored phone_number for form fields.
 * Uses libphonenumber only when loading an existing value, not while typing.
 */
export function splitStoredPhone(stored, defaultIsoArg) {
    const defaultIso = defaultIsoArg || resolvedDefaultIso();
    if (!stored || !String(stored).trim()) {
        return {
            iso: defaultIso,
            countryCode: getCountryCodeForIso(defaultIso),
            local: '',
        };
    }

    const raw = String(stored).trim();
    let parsed = null;

    if (raw.startsWith('+')) {
        parsed = parsePhoneNumberFromString(raw);
    } else {
        const digits = raw.replace(/\D/g, '');
        parsed = parsePhoneNumberFromString(digits, defaultIso);

        if (!parsed) {
            parsed = parsePhoneNumberFromString(`+${digits}`, defaultIso);
        }

        if (!parsed && /^[0-9]{10}$/.test(digits)) {
            parsed = parsePhoneNumberFromString(digits, DEFAULT_PHONE_COUNTRY);
        }
    }

    if (parsed?.country && parsed.nationalNumber) {
        return {
            iso: parsed.country,
            countryCode: `+${parsed.countryCallingCode}`,
            local: parsed.nationalNumber,
        };
    }

    const digits = raw.replace(/\D/g, '');
    return {
        iso: defaultIso,
        countryCode: getCountryCodeForIso(defaultIso),
        local: normalizeLocalDigits(defaultIso, digits) || digits,
    };
}

/** Combine country picker + local digits into E.164 when valid, else dial code + digits. */
export function combinePhone(iso, local) {
    const digits = normalizeLocalDigits(iso, local);
    if (!iso || !digits) {
        return '';
    }

    try {
        const parsed = parsePhoneNumberFromString(digits, iso);
        if (parsed?.isValid()) {
            return parsed.format('E.164');
        }

        const dialCode = getCountryCallingCode(iso);
        return `+${dialCode}${digits}`;
    } catch {
        return '';
    }
}

/** Accept any non-empty phone; no libphonenumber validity checks. */
export function validatePhoneParts(iso, local) {
    const combined = combinePhone(iso, local);
    if (!combined) {
        return { valid: false, message: i18n.t('phone.required', { defaultValue: 'Phone number is required.' }) };
    }
    return { valid: true, normalized: combined };
}

export function validateStoredPhone(stored, defaultIsoArg) {
    const defaultIso = defaultIsoArg || resolvedDefaultIso();
    const parts = splitStoredPhone(stored, defaultIso);
    return validatePhoneParts(parts.iso, parts.local);
}

/**
 * Normalize a phone from CSV / sheet import to E.164 (+countryCode…).
 * Values with a leading + keep their country code; bare local numbers get the
 * academy default from regional_settings (same as manual entry).
 */
export function normalizeImportedPhone(raw, defaultIsoArg) {
    const trimmed = raw != null ? String(raw).trim() : '';
    if (!trimmed) {
        return { valid: false, message: i18n.t('phone.required', { defaultValue: 'Phone number is required.' }) };
    }

    const defaultIso = defaultIsoArg || resolvedDefaultIso();

    if (trimmed.startsWith('+')) {
        const digits = trimmed.replace(/\D/g, '');
        if (!digits) {
            return { valid: false, message: i18n.t('phone.digitsRequired', { defaultValue: 'Phone number must contain digits.' }) };
        }
        return { valid: true, normalized: `+${digits}` };
    }

    return validateStoredPhone(trimmed, defaultIso);
}

export function phonesEquivalent(a, b, defaultIsoArg) {
    const defaultIso = defaultIsoArg || resolvedDefaultIso();
    const left = (combinePhone(
        splitStoredPhone(a, defaultIso).iso,
        splitStoredPhone(a, defaultIso).local,
    ) || '').trim();
    const right = (combinePhone(
        splitStoredPhone(b, defaultIso).iso,
        splitStoredPhone(b, defaultIso).local,
    ) || '').trim();
    return left === right;
}
